import type { RunResponse } from '@malaberg/shared';
/**
 * RunCard — T25.
 *
 * Displays a single run entry in the runs sidebar.
 *
 * Card layout (per PRD §10.3 K18):
 *   Line 1 [bold]   {productId} - {angle}
 *   Line 2 [normal] {avatar}
 *   Line 3 [muted]  {date.toLocaleString()} [ - $X.XX ]
 *   Line 4          Status Badge
 *
 * Status badge mapping (PRD §10.3 K11):
 *   completed / approved / logged → green "Done"
 *   running / queued              → blue "In progress"
 *   failed                        → red "Failed"
 *   edited                        → neutral "Edited"
 *   draft                         → neutral "Generating"
 *
 * Cost format (PRD §10.3 K12):
 *   writer      → 2 decimal places  ($0.02)
 *   admin       → 4 decimal places  ($0.0234)
 *   system_owner→ 4 decimal places  ($0.0234)
 *   null cost   → omit " - $X.XX" entirely
 *
 * Clicking the card calls `useRunStreamStore.getState().loadRun(run)` which
 * loads the persisted output into the OutputPane.
 */
import { Badge, Box, Group, Stack, Text } from '@mantine/core';
import { runsApi } from '../../api/endpoints/runs';
import { useBriefFormStore } from '../../state/brief-form-store';
import { useRunStreamStore } from '../../state/run-stream-store';
import { generationModelLabel } from '../../utils/generation-models';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RunCardProps {
  run: RunResponse;
}

// ─── Status badge helpers ─────────────────────────────────────────────────────

type BadgeConfig = { label: string; color: string };

function getStatusBadge(status: RunResponse['status']): BadgeConfig {
  switch (status) {
    case 'completed':
    case 'approved':
    case 'logged':
      return { label: 'Done', color: 'green' };
    case 'running':
      return { label: 'In progress', color: 'blue' };
    case 'failed':
      return { label: 'Failed', color: 'red' };
    case 'edited':
      return { label: 'Edited', color: 'gray' };
    case 'draft':
      return { label: 'Generating', color: 'gray' };
    default:
      // Exhaustiveness fallback — should never reach here with typed status
      return { label: String(status), color: 'gray' };
  }
}

// ─── Cost format helper ───────────────────────────────────────────────────────

function formatCost(cost: number | null): string | null {
  if (cost === null) return null;
  return `$${cost.toFixed(2)}`;
}

function getModeBadge(run: RunResponse): BadgeConfig {
  switch (run.mode) {
    case 'scratch_premise_set':
      return { label: 'Premise set', color: 'accent' };
    case 'scratch_ad_draft':
      return { label: 'Scratch draft', color: 'blue' };
    case 'fb_ad_to_advertorial':
      return { label: 'Facebook advertorial', color: 'teal' };
    case 'competitor_advertorial_to_advertorial':
      return { label: 'Competitor advertorial', color: 'violet' };
    default:
      return { label: 'Existing structure', color: 'gray' };
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RunCard({ run }: RunCardProps) {
  const badge = getStatusBadge(run.status);
  const modeBadge = getModeBadge(run);
  const costStr = formatCost(run.estimatedCostUsd);

  const dateStr = new Date(run.createdAt).toLocaleString();
  const metaStr = costStr ? `${dateStr} - ${costStr}` : dateStr;

  async function handleClick() {
    // Optimistic: load the list-row data immediately so the panel updates
    // without waiting for a network round-trip.
    useRunStreamStore.getState().loadRun(run);

    // Then fetch the full run (which includes parameterPins) and pre-fill
    // the brief form on the left so the operator sees both the original
    // request and the produced output.
    try {
      const full = (await runsApi.get(run.id)) as RunResponse;
      useRunStreamStore.getState().loadRun(full);

      // Pre-fill the brief form using the replay action. This handles all three
      // field channels: library-backed fields (parameterPins present) become
      // entry kind; fields with no pin (ephemeral/AI-suggested) become ephemeral
      // kind using the run's denormalized text. Ingredients follow the same logic
      // using displayOrder as the slot index.
      useBriefFormStore.getState().loadFromRun(full);
    } catch {
      // Non-fatal — the output panel is already populated with the list-row data.
    }
  }

  return (
    <Box
      data-testid="run-card"
      onClick={handleClick}
      style={{
        padding: '8px 10px',
        borderRadius: 'var(--mantine-radius-sm)',
        border: '1px solid var(--mantine-color-default-border)',
        cursor: 'pointer',
        background: 'var(--mantine-color-body)',
      }}
    >
      <Stack gap={2}>
        {/* Line 1: V{n} · {productId} - {angle} */}
        <Text fw={700} fz="sm" lineClamp={1} data-testid="run-card-title">
          <span
            style={{
              color: 'var(--accent, #126b57)',
              fontVariantNumeric: 'tabular-nums',
              marginRight: 6,
            }}
            data-testid="run-card-display-id"
          >
            {run.displayId ?? `V${run.displayNumber ?? '?'}`}
          </span>
          {run.productId} - {run.angle}
        </Text>

        {/* Line 2: avatar */}
        <Text fz="sm" lineClamp={1} data-testid="run-card-avatar">
          {run.avatar}
        </Text>

        {run.mode === 'scratch_ad_draft' && run.selectedPremise && (
          <Text fz="xs" c="dimmed" lineClamp={1} data-testid="run-card-selected-premise">
            P{run.selectedPremise.rank} - {run.selectedPremise.title}
          </Text>
        )}

        {/* Line 3: date - cost (cost omitted if null) */}
        <Text fz="xs" c="dimmed" data-testid="run-card-meta">
          {metaStr}
        </Text>

        {/* Line 4: mode + status badges */}
        <Group gap={6} wrap="wrap">
          <Badge color={modeBadge.color} variant="light" size="sm" data-testid="run-mode-badge">
            {modeBadge.label}
          </Badge>
          <Badge color="accent" variant="light" size="sm">
            {run.generationModel?.label ?? generationModelLabel(null)}
          </Badge>
          <Badge color={badge.color} variant="light" size="sm" data-testid="status-badge">
            {badge.label}
          </Badge>
        </Group>
      </Stack>
    </Box>
  );
}
